import { useLayoutEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const toArray = (selector, scope) => gsap.utils.toArray(selector, scope);

export default function useLandingMotion(scopeRef) {
  useLayoutEffect(() => {
    const scope = scopeRef.current;
    if (!scope) return undefined;

    const media = gsap.matchMedia();
    const context = gsap.context(() => {
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (reducedMotion) {
        scope.classList.add("motion-reduced");
        return;
      }

      scope.classList.add("motion-enabled");

      const intro = gsap.timeline({
        defaults: { ease: "power3.out" },
      });

      intro
        .from("[data-hero-kicker]", { autoAlpha: 0, y: 18, duration: 0.65 })
        .from("[data-hero-title-line]", {
          autoAlpha: 0,
          yPercent: 115,
          duration: 0.85,
          stagger: 0.08,
          ease: "power4.out",
        }, "-=0.4")
        .from("[data-hero-copy]", {
          autoAlpha: 0,
          y: 22,
          filter: "blur(8px)",
          duration: 0.7,
        }, "-=0.48")
        .from("[data-hero-calculator]", {
          autoAlpha: 0,
          y: 42,
          scale: 0.985,
          duration: 0.9,
        }, "-=0.4")
        .from("[data-hero-stat]", {
          autoAlpha: 0,
          y: 24,
          duration: 0.55,
          stagger: 0.07,
        }, "-=0.65")
        .from("[data-hero-actions]", {
          autoAlpha: 0,
          y: 18,
          duration: 0.6,
        }, "-=0.38");

      toArray("[data-motion-section]", scope).forEach((section) => {
        const heading = section.querySelector("[data-motion-heading]");
        const cards = section.querySelectorAll("[data-motion-card]");
        const divider = section.querySelector("[data-motion-divider]");

        if (heading) {
          gsap.from(heading, {
            autoAlpha: 0,
            y: 42,
            filter: "blur(10px)",
            duration: 0.9,
            ease: "power3.out",
            clearProps: "transform,opacity,visibility,filter",
            scrollTrigger: {
              trigger: section,
              start: "top 78%",
              once: true,
            },
          });
        }

        if (cards.length) {
          gsap.from(cards, {
            autoAlpha: 0,
            y: 48,
            scale: 0.98,
            duration: 0.8,
            stagger: 0.1,
            ease: "power3.out",
            clearProps: "transform,opacity,visibility",
            scrollTrigger: {
              trigger: section,
              start: "top 64%",
              once: true,
            },
          });
        }

        if (divider) {
          gsap.from(divider, {
            scaleX: 0,
            transformOrigin: "left center",
            duration: 1.1,
            ease: "power3.inOut",
            scrollTrigger: {
              trigger: section,
              start: "top 82%",
              once: true,
            },
          });
        }
      });

      toArray("[data-feature-row]", scope).forEach((row, index) => {
        const direction = row.dataset.direction === "reverse" ? -1 : 1;
        gsap.from(row, {
          autoAlpha: 0,
          x: direction * 42,
          y: 24,
          duration: 0.85,
          ease: "power3.out",
          clearProps: "transform,opacity,visibility",
          scrollTrigger: {
            trigger: row,
            start: "top 84%",
            once: true,
          },
          delay: index * 0.03,
        });
      });

      const progress = scope.querySelector("[data-page-progress]");
      if (progress) {
        gsap.to(progress, {
          scaleX: 1,
          transformOrigin: "left center",
          ease: "none",
          scrollTrigger: {
            trigger: scope,
            start: "top top",
            end: "bottom bottom",
            scrub: 0.25,
          },
        });
      }

      media.add("(min-width: 1024px)", () => {
        toArray("[data-parallax-media]", scope).forEach((mediaNode) => {
          const trigger = mediaNode.closest("section") || mediaNode;
          gsap.to(mediaNode, {
            yPercent: 10,
            scale: 1.055,
            ease: "none",
            scrollTrigger: {
              trigger,
              start: "top bottom",
              end: "bottom top",
              scrub: 1.1,
            },
          });
        });

        toArray("[data-parallax-field]", scope).forEach((field) => {
          const trigger = field.closest("section") || field;
          const shards = field.querySelectorAll("[data-glass-shard]");
          const bands = field.querySelectorAll("[data-light-band]");

          gsap.to(shards, {
            y: (index, node) => Number(node.dataset.depth || 0) * window.innerHeight * 0.24,
            x: (index, node) => Number(node.dataset.depth || 0) * 60,
            ease: "none",
            stagger: 0.015,
            scrollTrigger: {
              trigger,
              start: "top bottom",
              end: "bottom top",
              scrub: 1.25,
            },
          });

          gsap.to(bands, {
            yPercent: (index) => (index === 0 ? 18 : -14),
            xPercent: (index) => (index === 0 ? 6 : -6),
            ease: "none",
            scrollTrigger: {
              trigger,
              start: "top bottom",
              end: "bottom top",
              scrub: 1.5,
            },
          });
        });

        const hero = scope.querySelector("[data-hero]");
        const heroCopy = hero?.querySelector("[data-hero-copy-block]");
        const heroMedia = hero?.querySelector("[data-hero-media]");
        const heroCalculator = hero?.querySelector("[data-hero-calculator]");

        if (hero && heroCopy && heroCalculator) {
          const heroScrub = gsap.timeline({
            scrollTrigger: {
              trigger: hero,
              start: "top top",
              end: "bottom top",
              scrub: 0.9,
            },
          });

          heroScrub
            .to(heroCopy, { yPercent: -12, autoAlpha: 0.35, ease: "none" }, 0)
            .to(heroCalculator, { yPercent: -5, scale: 0.965, ease: "none" }, 0);
        }

        if (heroMedia) {
          gsap.to(heroMedia, {
            yPercent: 16,
            scale: 1.08,
            ease: "none",
            scrollTrigger: {
              trigger: hero,
              start: "top top",
              end: "bottom top",
              scrub: 1.1,
            },
          });
        }

        const story = scope.querySelector("[data-story]");
        const storyStage = story?.querySelector("[data-story-stage]");
        const storySteps = story ? toArray("[data-story-step]", story) : [];
        const storyProgress = story?.querySelector("[data-story-progress]");

        if (story && storyStage && storySteps.length > 1) {
          scope.classList.add("motion-pinned");
          gsap.set(storySteps.slice(1), { autoAlpha: 0, y: 44, scale: 0.96 });

          const storyTimeline = gsap.timeline({
            scrollTrigger: {
              trigger: story,
              start: "top top+=80",
              end: () => `+=${Math.max(window.innerHeight * 2.2, 1500)}`,
              pin: storyStage,
              scrub: 0.9,
              anticipatePin: 1,
              invalidateOnRefresh: true,
            },
          });

          storySteps.forEach((step, index) => {
            if (index === 0) return;
            storyTimeline
              .to(storySteps[index - 1], {
                autoAlpha: 0,
                y: -42,
                scale: 0.98,
                duration: 0.42,
                ease: "power2.inOut",
              })
              .to(step, {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                duration: 0.58,
                ease: "power3.out",
              }, "-=0.16");
          });

          if (storyProgress) {
            storyTimeline.to(storyProgress, {
              scaleX: 1,
              transformOrigin: "left center",
              ease: "none",
              duration: 1,
            }, 0);
          }
        }
      });

      requestAnimationFrame(() => ScrollTrigger.refresh());
    }, scope);

    return () => {
      media.revert();
      context.revert();
      scope.classList.remove("motion-enabled", "motion-pinned", "motion-reduced");
    };
  }, [scopeRef]);
}
